import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cx } from "@/components/FormFields";

const Field = ({ label, value, onChange, input, area, testId }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wide text-[#9EABA0] mb-1.5">{label}</label>
    {area ? (
      <textarea rows={area} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cx(input, "w-full resize-y")} data-testid={testId} />
    ) : (
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cx(input, "w-full")} data-testid={testId} />
    )}
  </div>
);

const Card = ({ title, children }) => (
  <div className="rounded-xl border border-[#232f25] bg-[#111712] p-6 space-y-4">
    <h3 className="font-display text-lg font-bold text-white tracking-tight">{title}</h3>
    {children}
  </div>
);

export function SiteContentPanel({ input }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/content/draft").then((r) => setData(r.data)).catch(() => toast.error("Failed to load content."));
  }, []);

  if (!data) return <p className="text-[#69776b]">Loading…</p>;

  const s = (path, val) => setData((d) => {
    const next = JSON.parse(JSON.stringify(d));
    let o = next;
    for (let i = 0; i < path.length - 1; i++) o = o[path[i]] = o[path[i]] || {};
    o[path[path.length - 1]] = val;
    return next;
  });

  const saveDraft = async () => {
    setBusy(true);
    try { await api.put("/content/draft", { data }); toast.success("Draft saved."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed."); }
    finally { setBusy(false); }
  };
  const preview = async () => { await saveDraft(); window.open("/?preview=1", "_blank"); };
  const publish = async () => {
    await saveDraft();
    try { await api.post("/content/publish"); toast.success("Published! Changes are live."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed."); }
  };

  const rows = data.warranty?.rows || [];

  return (
    <div className="space-y-6" data-testid="site-content-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 sticky top-16 z-20 bg-[#0a0d0a]/90 backdrop-blur py-3 -mt-2">
        <p className="text-sm text-[#69776b]">Edit site text & contact info. Save a draft, preview, then publish to go live.</p>
        <div className="flex gap-2">
          <button onClick={saveDraft} disabled={busy} data-testid="content-save-draft" className="px-4 py-2.5 rounded-md bg-[#0d120e] border border-[#232f25] text-sm font-semibold text-[#f4f7f4] hover:border-[#8CE63C]/50 disabled:opacity-50">Save Draft</button>
          <button onClick={preview} data-testid="content-preview" className="px-4 py-2.5 rounded-md bg-[#0d120e] border border-[#8CE63C]/40 text-sm font-semibold text-[#8CE63C] hover:bg-[#132717]">Preview</button>
          <button onClick={publish} data-testid="content-publish" className="px-4 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold hover:bg-[#9df04f]">Publish</button>
        </div>
      </div>

      <Card title="Homepage Hero">
        <Field label="Eyebrow" input={input} value={data.hero?.eyebrow} onChange={(v) => s(["hero", "eyebrow"], v)} testId="content-hero-eyebrow" />
        <Field label="Title" input={input} value={data.hero?.title} onChange={(v) => s(["hero", "title"], v)} testId="content-hero-title" />
        <Field label="Subtitle" input={input} area={3} value={data.hero?.subtitle} onChange={(v) => s(["hero", "subtitle"], v)} testId="content-hero-subtitle" />
      </Card>

      <Card title="About Us">
        <Field label="Heading" input={input} value={data.about?.heading} onChange={(v) => s(["about", "heading"], v)} testId="content-about-heading" />
        <Field label="Text" input={input} area={4} value={data.about?.text} onChange={(v) => s(["about", "text"], v)} testId="content-about-text" />
        <Field label="Categories (comma separated)" input={input} value={(data.about?.items || []).join(", ")} onChange={(v) => s(["about", "items"], v.split(",").map((x) => x.trim()).filter(Boolean))} testId="content-about-items" />
      </Card>

      <Card title="Warranty">
        <Field label="Intro" input={input} area={2} value={data.warranty?.intro} onChange={(v) => s(["warranty", "intro"], v)} testId="content-warranty-intro" />
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex gap-2 items-start">
              <input value={r.coverage} onChange={(e) => { const nr = [...rows]; nr[i] = { ...nr[i], coverage: e.target.value }; s(["warranty", "rows"], nr); }} placeholder="Coverage" className={cx(input, "w-1/3")} />
              <input value={r.policy} onChange={(e) => { const nr = [...rows]; nr[i] = { ...nr[i], policy: e.target.value }; s(["warranty", "rows"], nr); }} placeholder="Policy" className={cx(input, "flex-1")} />
              <button onClick={() => s(["warranty", "rows"], rows.filter((_, x) => x !== i))} className="text-[#FCA5A5] px-2 py-2">✕</button>
            </div>
          ))}
          <button onClick={() => s(["warranty", "rows"], [...rows, { coverage: "", policy: "" }])} className="text-xs text-[#8CE63C] font-semibold">+ Add Row</button>
        </div>
        <Field label="Footnote" input={input} area={2} value={data.warranty?.footnote} onChange={(v) => s(["warranty", "footnote"], v)} testId="content-warranty-footnote" />
      </Card>

      <Card title="Contact Information">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone (display)" input={input} value={data.contact?.phone} onChange={(v) => s(["contact", "phone"], v)} testId="content-contact-phone" />
          <Field label="Phone (dial, digits)" input={input} value={data.contact?.phoneHref} onChange={(v) => s(["contact", "phoneHref"], v)} testId="content-contact-phonehref" />
        </div>
        <Field label="Email" input={input} value={data.contact?.email} onChange={(v) => s(["contact", "email"], v)} testId="content-contact-email" />
        <Field label="Note" input={input} area={2} value={data.contact?.note} onChange={(v) => s(["contact", "note"], v)} testId="content-contact-note" />
      </Card>

      <Card title="Footer">
        <Field label="Tagline" input={input} area={2} value={data.footer?.tagline} onChange={(v) => s(["footer", "tagline"], v)} testId="content-footer-tagline" />
      </Card>
    </div>
  );
}
